import React, { Component } from 'react';
import PropTypes from 'prop-types';

const RESIZE_DEBOUNCE_TIMER_MS = 400;
const WINDOW_EVENT_RESIZE_END = new window.Event('resize-end');

/**
 * A Higher Order Component that binds an event handler to Window `resize` event and dispatches a custom
 * event `resize-end` when the resizing has finished. Use this HOC to enhance components who need to
 * respond to window resize events by passing callback handlers to onWindowResizeStart and onWindowResizeEnd props.
 *
 * @param {React.Component} WrappedComponent - the component to enhance
 * @returns {React.Component}
 **/
const windowResizeFactory = WrappedComponent => {
  class WindowResizeComponent extends Component {
    static propTypes = {
      hideOnResize: PropTypes.bool,
      onWindowResizeStart: PropTypes.func,
      onWindowResizeEnd: PropTypes.func,
      resizeDebounceMs: PropTypes.number,
    };

    static defaultProps = {
      hideOnResize: false,
      onWindowResizeStart: () => {},
      onWindowResizeEnd: () => {},
      resizeDebounceMs: RESIZE_DEBOUNCE_TIMER_MS,
    };

    constructor(props) {
      super(props);

      this._onWindowResizeStart = this._onWindowResize.bind(
        this,
        this._onWindowResizeStart.bind(this)
      );
      this._onWindowResizeEnd = this._onWindowResizeEnd.bind(this);
    }

    state = {
      isResizing: false,
    };

    componentDidMount() {
      window.addEventListener('resize', this._onWindowResizeStart);
      window.addEventListener('resize-end', this._onWindowResizeEnd);
    }

    componentWillUnmount() {
      window.removeEventListener('resize', this._onWindowResizeStart);
      window.removeEventListener('resize-end', this._onWindowResizeEnd);
    }

    /**
     * A handler attached to the window resize event which will invoke the passed in callback
     * function on resize, and trigger a custom resize-end event on window when resize has finished.
     *
     * @param {Function} callbackFunc - the callback to invoke on window resize
     **/
    _onWindowResize(callbackFunc) {
      if (callbackFunc) callbackFunc();

      if (this._timerId) {
        clearTimeout(this._timerId);
      }

      this._timerId = setTimeout(() => {
        window.dispatchEvent(WINDOW_EVENT_RESIZE_END);
      }, this.props.resizeDebounceMs);
    }

    /**
     * Event handler for `window.resize`. Fired when the browser is resizing, debounced against
     * resizeDebounceMs number provided via props.
     **/
    _onWindowResizeStart() {
      this.setState({ isResizing: true });

      if (this.props.onWindowResizeStart) {
        this.props.onWindowResizeStart();
      }
    }

    /**
     * Event handler for `window.resize-end`. Fired when the browser completes resizing.
     **/
    _onWindowResizeEnd() {
      this.setState({ isResizing: false });

      if (this.props.onWindowResizeEnd) {
        this.props.onWindowResizeEnd();
      }
    }

    /**
     * Helper function to determine if the WrappedComponent should be rendered or null.
     * @returns {Bool} - true if it should be hidden, false if it should not be hidden.
     **/
    _shouldHideWrappedComponent() {
      const { isResizing } = this.state;
      const { hideOnResize } = this.props;

      return hideOnResize && isResizing;
    }

    render() {
      // eslint-disable-next-line no-unused-vars
      const {
        onWindowResizeStart, // eslint-disable-line no-unused-vars
        onWindowResizeEnd, // eslint-disable-line no-unused-vars
        ...otherProps
      } = this.props;

      return !this._shouldHideWrappedComponent() ? (
        <WrappedComponent {...otherProps} />
      ) : null;
    }
  }

  return WindowResizeComponent;
};

export default windowResizeFactory;
export { windowResizeFactory };
